import { useEffect } from "react";

/**
 * Register a keyboard shortcut on window.
 *
 * combo format: "mod+k" (mod = Cmd on mac / Ctrl elsewhere), or a single key.
 * Shortcut is IGNORED when focus is in an editable element (input, textarea,
 * contenteditable) unless `captureInInputs` is true.
 */
export function useHotkey(
  combo: string,
  handler: (e: KeyboardEvent) => void,
  opts: { captureInInputs?: boolean } = {},
): void {
  useEffect(() => {
    const parts = combo.toLowerCase().split("+").map((p) => p.trim());
    const wantMod = parts.includes("mod") || parts.includes("cmd") || parts.includes("ctrl");
    const wantShift = parts.includes("shift");
    const wantAlt = parts.includes("alt") || parts.includes("option");
    const key = parts[parts.length - 1] ?? "";

    function isEditable(el: EventTarget | null): boolean {
      if (!(el instanceof HTMLElement)) return false;
      const tag = el.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return true;
      return el.isContentEditable;
    }

    function onKey(e: KeyboardEvent) {
      if (!opts.captureInInputs && isEditable(e.target)) return;
      const modPressed = e.metaKey || e.ctrlKey;
      if (wantMod !== modPressed) return;
      if (wantShift !== e.shiftKey) return;
      if (wantAlt !== e.altKey) return;
      if (e.key.toLowerCase() !== key) return;
      e.preventDefault();
      handler(e);
    }

    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [combo, handler, opts.captureInInputs]);
}
